import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Empty, Form, Image, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import {
  approveControlTaskCorrectionReport,
  approveControlTaskReport,
  createControlTaskCorrection,
  fetchControlTaskCorrectionComparison,
  listControlTaskCriticalNonconformities,
  ControlTaskReportApprovalRow,
  downloadControlTaskReport,
  listControlTaskReportApprovals,
  revokeControlTaskCorrectionReportApproval,
  revokeControlTaskReportApproval,
} from '../../modules/inspectors/services/controlTaskService';
import {
  getPlanningInspectorContext,
} from '../../modules/inspection-appointments/services/inspection-appointmentService';
import { listPublicHolidaysAll } from '../../modules/public-holidays/services/public-holidayService';
import type { PublicHoliday } from '../../modules/public-holidays/types/PublicHoliday';
import DocumentService from '../../services/DocumentService';
import { openNotification } from '../../utils';
import { buildBlockingPublicHolidayDateSet, buildVisiblePublicHolidayMap } from '../../views/Planning/publicHolidayUtils';
import { CriticalNonconformityRow, extractCriticalNonconformityImage } from './criticalNonconformities';

const { Text } = Typography;
const textCollator = new Intl.Collator('tr', { sensitivity: 'base', numeric: true });
type PlanCaseType = 'CORRECTION' | 'FOLLOW_UP';
type InspectorOption = { value: number; label: string };
type PlanningContext = {
  inspectors: any[];
  appointments: any[];
  leaves: any[];
  reservations: any[];
};
type PlanSlotStatus = {
  disabled: boolean;
  reason?: string;
  details?: string[];
};

const emptyPlanningContext: PlanningContext = {
  inspectors: [],
  appointments: [],
  leaves: [],
  reservations: [],
};

const planDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const planHourSlots = Array.from({ length: 9 }, (_, index) => {
  const hour = 9 + index;
  return {
    hour,
    label: `${String(hour).padStart(2, '0')}:00 - ${String(hour + 1).padStart(2, '0')}:00`,
  };
});

type ReportApprovalSummary = {
  completedInspectionTaskCount: number;
  approvedInspectionTaskCount: number;
  inspectionTaskCount: number;
};

interface ReportApprovalDrawerProps {
  open: boolean;
  proposalId: number | null;
  proposalNumber?: string | null;
  onClose: () => void;
  onSummaryChange?: (proposalId: number, summary: ReportApprovalSummary) => void;
}

const buildSummaryFromRows = (rows: ControlTaskReportApprovalRow[]): ReportApprovalSummary => {
  let inspectionTaskCount = 0;
  let completedInspectionTaskCount = 0;
  let approvedInspectionTaskCount = 0;

  rows.forEach((row) => {
    const taskId = Number(row?.taskId);
    const attributeId = Number(row?.attributeId);
    const orderNo = Number(row?.orderNo);
    if (
      !row?.hasResponse
      || !Number.isFinite(taskId)
      || taskId <= 0
      || !Number.isFinite(attributeId)
      || attributeId <= 0
      || !Number.isFinite(orderNo)
    ) {
      return;
    }

    inspectionTaskCount += 1;
    if (row?.controlCompleted) {
      completedInspectionTaskCount += 1;
    }
    if (row?.reportApproved) {
      approvedInspectionTaskCount += 1;
    }
  });

  return {
    completedInspectionTaskCount,
    approvedInspectionTaskCount,
    inspectionTaskCount,
  };
};

const sortReportApprovalRows = (rows: ControlTaskReportApprovalRow[]) => (
  [...rows].sort((left, right) => {
    const attributeCompare = textCollator.compare(left?.attributeName || '', right?.attributeName || '');
    if (attributeCompare !== 0) {
      return attributeCompare;
    }

    const leftOrderNo = Number(left?.orderNo);
    const rightOrderNo = Number(right?.orderNo);
    const normalizedLeftOrderNo = Number.isFinite(leftOrderNo) ? leftOrderNo : Number.MAX_SAFE_INTEGER;
    const normalizedRightOrderNo = Number.isFinite(rightOrderNo) ? rightOrderNo : Number.MAX_SAFE_INTEGER;
    if (normalizedLeftOrderNo !== normalizedRightOrderNo) {
      return normalizedLeftOrderNo - normalizedRightOrderNo;
    }

    const taskCompare = Number(left?.taskId || 0) - Number(right?.taskId || 0);
    if (taskCompare !== 0) {
      return taskCompare;
    }

    return Number(left?.attributeId || 0) - Number(right?.attributeId || 0);
  })
);

const openUrlInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
};

const getRowKey = (row: ControlTaskReportApprovalRow) => `${row?.taskId ?? 'no-task'}-${row?.attributeId ?? 'no-attribute'}-${row?.orderNo ?? 'no-order'}`;

const openPlanStatuses = new Set(['PLANNING_REQUIRED', 'PLANNED', 'IN_PROGRESS', 'SUBMITTED']);

const isOpenPlanStatus = (status?: string | null) => (
  status ? openPlanStatuses.has(status.trim().toUpperCase()) : false
);

const resolveInspectorOptionLabel = (candidate: any): string => {
  const account = candidate?.account ?? {};
  const parts = [
    account?.firstName,
    account?.lastName,
  ].filter(Boolean);
  if (parts.length) {
    return parts.join(' ');
  }
  return account?.fullName || account?.name || candidate?.name || `PKM #${candidate?.id}`;
};

const candidateMatchesAttribute = (candidate: any, attributeId?: number | null): boolean => {
  if (!attributeId) {
    return false;
  }
  const attributes = Array.isArray(candidate?.attributes) ? candidate.attributes : [];
  return attributes.some((attribute: any) => Number(attribute?.id) === Number(attributeId));
};

const startOfWeekMonday = (value: dayjs.Dayjs) => {
  const normalizedDay = (value.day() + 6) % 7;
  return value.startOf('day').subtract(normalizedDay, 'day');
};

const formatWeekRange = (weekStart: dayjs.Dayjs) => (
  `${weekStart.format('DD.MM.YYYY')} - ${weekStart.add(6, 'day').format('DD.MM.YYYY')}`
);

const isDateOnlyMoment = (value: dayjs.Dayjs) => (
  value.hour() === 0 && value.minute() === 0 && value.second() === 0 && value.millisecond() === 0
);

const buildBusyInterval = (
  startInput?: dayjs.Dayjs | null,
  endInput?: dayjs.Dayjs | null,
  options?: { allDay?: boolean },
): { start: dayjs.Dayjs; end: dayjs.Dayjs } | null => {
  if (!startInput || !dayjs.isDayjs(startInput) || !startInput.isValid()) {
    return null;
  }
  const start = startInput;
  const endCandidate = endInput && dayjs.isDayjs(endInput) && endInput.isValid() ? endInput : null;

  if (options?.allDay) {
    return {
      start: start.startOf('day'),
      end: (endCandidate || start).endOf('day'),
    };
  }

  if (endCandidate) {
    if (isDateOnlyMoment(start) && isDateOnlyMoment(endCandidate)) {
      return {
        start: start.startOf('day'),
        end: endCandidate.endOf('day'),
      };
    }
    return {
      start,
      end: endCandidate.isAfter(start) ? endCandidate : start.add(1, 'minute'),
    };
  }

  return {
    start,
    end: isDateOnlyMoment(start) ? start.endOf('day') : start.add(1, 'minute'),
  };
};

const isTimeRangeOverlapping = (
  startA: dayjs.Dayjs,
  endA: dayjs.Dayjs,
  startB: dayjs.Dayjs,
  endB: dayjs.Dayjs,
) => startA.isBefore(endB) && endA.isAfter(startB);

const normalizeText = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const text = String(value).trim();
  return text.length ? text : undefined;
};

const resolveEntryAccountId = (entry: any): number | null => {
  const value =
    entry?.account?.id ??
    entry?.accountId ??
    entry?.inspectorId ??
    entry?.inspector?.id;
  const accountId = Number(value);
  return Number.isFinite(accountId) && accountId > 0 ? accountId : null;
};

const isDeletedOrCanceledAppointment = (appointment: any): boolean => {
  if (!appointment || typeof appointment !== 'object') {
    return false;
  }
  const status = normalizeText(appointment?.status)?.toUpperCase();
  return (
    status === 'CANCELED' ||
    status === 'CANCELLED' ||
    status === 'DELETED' ||
    appointment?.isDeleted === true ||
    appointment?.deleted === true ||
    appointment?.deletedAt != null ||
    appointment?.deleted_at != null
  );
};

const resolveAppointmentLabel = (appointment: any): string => (
  normalizeText(appointment?.proposalNumber) ||
  normalizeText(appointment?.proposal?.proposalNumber) ||
  normalizeText(appointment?.companyName) ||
  normalizeText(appointment?.company?.name) ||
  'Görev'
);

const leaveTypeLabelMap: Record<string, string> = {
  ANNUAL: 'Yıllık İzin',
  REPORT: 'Raporlu',
  UNPAID: 'Ücretsiz İzin',
  SICK: 'Sağlık İzni',
  ADMINISTRATIVE: 'İdari İzin',
  LEAVE: 'İzin',
};

const resolveLeaveLabel = (leave: any): string => {
  const typeKey = typeof leave?.type === 'string' ? leave.type.trim().toUpperCase() : '';
  return leaveTypeLabelMap[typeKey] || 'İzin';
};

const extractUploadedDocumentUrl = async (uploaded: any): Promise<string | null> => {
  const directUrl = uploaded?.data?.preSignUrl || uploaded?.data?.preSignedUrl || uploaded?.data?.url;
  if (typeof directUrl === 'string' && directUrl.trim()) {
    return directUrl.trim();
  }
  const docId = uploaded?.data?.id;
  if (!docId) {
    return null;
  }
  const response = await DocumentService.getDocument(docId);
  const fetchedUrl = response?.data?.preSignUrl || response?.data?.preSignedUrl || response?.data?.url;
  return typeof fetchedUrl === 'string' && fetchedUrl.trim() ? fetchedUrl.trim() : null;
};

const correctionStatusLabelMap: Record<string, string> = {
  PLANNING_REQUIRED: 'Planlama Gerekli',
  PLANNED: 'Planlandı',
  IN_PROGRESS: 'Devam Ediyor',
  SUBMITTED: 'Onay Bekliyor',
  APPROVED: 'Onaylandı',
  CANCELLED: 'İptal Edildi',
  CANCELED: 'İptal Edildi',
};

const correctionStatusColorMap: Record<string, string> = {
  PLANNING_REQUIRED: 'orange',
  PLANNED: 'gold',
  IN_PROGRESS: 'blue',
  SUBMITTED: 'cyan',
  APPROVED: 'green',
  CANCELLED: 'red',
  CANCELED: 'red',
};

const renderCorrectionStatusTag = (status?: string | null, fallback = 'Planlanmadı') => {
  const normalizedStatus = typeof status === 'string' ? status.trim().toUpperCase() : '';
  if (!normalizedStatus) {
    return <Tag>{fallback}</Tag>;
  }
  return (
    <Tag color={correctionStatusColorMap[normalizedStatus] || 'default'}>
      {correctionStatusLabelMap[normalizedStatus] || status}
    </Tag>
  );
};

const extractDocumentUrl = (documentPayload: any): string | null =>
  documentPayload?.preSignUrl ??
  documentPayload?.preSignedUrl ??
  documentPayload?.url ??
  documentPayload?.documentUrl ??
  null;

const CriticalNonconformityImagePreview: React.FC<{ row: CriticalNonconformityRow }> = ({ row }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(row?.imageUrl || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageUrl(row?.imageUrl || null);

    if (row?.imageUrl || !row?.documentId) {
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    DocumentService.getDocument(row.documentId)
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        setImageUrl(extractDocumentUrl(response?.data));
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Uygunsuzluk fotoğrafı yüklenemedi:', error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [row?.documentId, row?.imageUrl]);

  if (loading) {
    return <Text type="secondary">Yükleniyor</Text>;
  }

  if (!imageUrl) {
    return <Text type="secondary">-</Text>;
  }

  return (
    <Image
      src={imageUrl}
      alt={row?.imageName || 'Uygunsuzluk fotoğrafı'}
      width={72}
      height={54}
      style={{ objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
      preview={{ mask: 'Büyüt' }}
    />
  );
};

const ReportApprovalDrawer: React.FC<ReportApprovalDrawerProps> = ({
  open,
  proposalId,
  proposalNumber,
  onClose,
  onSummaryChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ControlTaskReportApprovalRow[]>([]);
  const [actionKey, setActionKey] = useState<string | null>(null);
  const [downloadKey, setDownloadKey] = useState<string | null>(null);
  const [criticalDrawerOpen, setCriticalDrawerOpen] = useState(false);
  const [criticalLoading, setCriticalLoading] = useState(false);
  const [criticalRows, setCriticalRows] = useState<CriticalNonconformityRow[]>([]);
  const [selectedCriticalRow, setSelectedCriticalRow] = useState<ControlTaskReportApprovalRow | null>(null);
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [planCaseType, setPlanCaseType] = useState<PlanCaseType>('CORRECTION');
  const [selectedPlanRow, setSelectedPlanRow] = useState<ControlTaskReportApprovalRow | null>(null);
  const [inspectorOptions, setInspectorOptions] = useState<InspectorOption[]>([]);
  const [inspectorOptionsLoading, setInspectorOptionsLoading] = useState(false);
  const [planningWeekStart, setPlanningWeekStart] = useState(() => startOfWeekMonday(dayjs()));
  const [planningContext, setPlanningContext] = useState<PlanningContext>(emptyPlanningContext);
  const [planningContextLoading, setPlanningContextLoading] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [publicHolidayLoading, setPublicHolidayLoading] = useState(false);
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);
  const [slotSelectionError, setSlotSelectionError] = useState<string | null>(null);
  const [contractFileList, setContractFileList] = useState<UploadFile[]>([]);
  const [comparisonDrawerOpen, setComparisonDrawerOpen] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparison, setComparison] = useState<any | null>(null);
  const [planForm] = Form.useForm();
  const selectedPlanAccountId = Form.useWatch('assignedAccountId', planForm);
  const criticalRequestRef = useRef(0);
  const planningContextRequestRef = useRef(0);

  const updateRowsAndSummary = useCallback((nextRows: ControlTaskReportApprovalRow[]) => {
    const sortedRows = sortReportApprovalRows(nextRows);
    setRows(sortedRows);
    if (proposalId != null && onSummaryChange) {
      onSummaryChange(proposalId, buildSummaryFromRows(sortedRows));
    }
  }, [onSummaryChange, proposalId]);

  useEffect(() => {
    if (!open || proposalId == null) {
      if (!open) {
        setRows([]);
      }
      return;
    }

    let cancelled = false;
    setLoading(true);
    listControlTaskReportApprovals(proposalId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        updateRowsAndSummary(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error('Rapor onay verileri alınamadı:', error);
        if (!cancelled) {
          updateRowsAndSummary([]);
          openNotification('error', 'Hata', 'Rapor onay verileri alınamadı.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, proposalId, updateRowsAndSummary]);

  const handleDownload = useCallback(async (row: ControlTaskReportApprovalRow) => {
    const taskId = Number(row?.taskId);
    const attributeId = Number(row?.attributeId);
    const orderNo = Number(row?.orderNo);
    if (proposalId == null || !row?.hasResponse || !Number.isFinite(taskId) || !Number.isFinite(attributeId) || !Number.isFinite(orderNo)) {
      return;
    }

    const rowKey = getRowKey(row);
    let objectUrl: string | null = null;
    try {
      setDownloadKey(rowKey);
      if (row?.reportApproved && row?.reportUrl) {
        openUrlInNewTab(row.reportUrl);
        return;
      }
      const { url } = await downloadControlTaskReport(proposalId, taskId, attributeId, orderNo);
      objectUrl = url;
      openUrlInNewTab(url);
    } catch (error) {
      console.error('Rapor indirme hatası:', error);
      openNotification('error', 'Hata', 'Rapor indirilemedi.');
    } finally {
      if (objectUrl) {
        const urlToRevoke = objectUrl;
        window.setTimeout(() => URL.revokeObjectURL(urlToRevoke), 60_000);
      }
      setDownloadKey(null);
    }
  }, [proposalId]);

  const handleApprove = useCallback(async (row: ControlTaskReportApprovalRow) => {
    const taskId = Number(row?.taskId);
    const attributeId = Number(row?.attributeId);
    const orderNo = Number(row?.orderNo);
    if (proposalId == null || !Number.isFinite(taskId) || !Number.isFinite(attributeId) || !Number.isFinite(orderNo)) {
      return;
    }

    const rowKey = getRowKey(row);
    try {
      setActionKey(rowKey);
      const correctionLikeCaseId = row?.correctionCaseId ?? row?.followUpCaseId;
      const updated = row?.responseKind === 'CORRECTION' && correctionLikeCaseId
        ? await approveControlTaskCorrectionReport(Number(correctionLikeCaseId))
        : await approveControlTaskReport(proposalId, taskId, attributeId, orderNo);
      updateRowsAndSummary(rows.map((item) => (
        getRowKey(item) === getRowKey(updated) ? updated : item
      )));
      openNotification('success', 'Başarılı', 'Rapor onaylandı.');
    } catch (error) {
      console.error('Rapor onaylama hatası:', error);
      openNotification('error', 'Hata', 'Rapor onaylanamadı.');
    } finally {
      setActionKey(null);
    }
  }, [proposalId, rows, updateRowsAndSummary]);

  const handleRevoke = useCallback(async (row: ControlTaskReportApprovalRow) => {
    const taskId = Number(row?.taskId);
    const attributeId = Number(row?.attributeId);
    const orderNo = Number(row?.orderNo);
    if (proposalId == null || !Number.isFinite(taskId) || !Number.isFinite(attributeId) || !Number.isFinite(orderNo)) {
      return;
    }

    const rowKey = getRowKey(row);
    try {
      setActionKey(rowKey);
      const correctionLikeCaseId = row?.correctionCaseId ?? row?.followUpCaseId;
      const updated = row?.responseKind === 'CORRECTION' && correctionLikeCaseId
        ? await revokeControlTaskCorrectionReportApproval(Number(correctionLikeCaseId))
        : await revokeControlTaskReportApproval(proposalId, taskId, attributeId, orderNo);
      updateRowsAndSummary(rows.map((item) => (
        getRowKey(item) === getRowKey(updated) ? updated : item
      )));
      openNotification('success', 'Başarılı', 'Rapor onayı geri alındı.');
    } catch (error) {
      console.error('Rapor onayı geri alma hatası:', error);
      openNotification('error', 'Hata', 'Rapor onayı geri alınamadı.');
    } finally {
      setActionKey(null);
    }
  }, [proposalId, rows, updateRowsAndSummary]);

  const closeCriticalDrawer = useCallback(() => {
    criticalRequestRef.current += 1;
    setCriticalDrawerOpen(false);
    setCriticalLoading(false);
    setCriticalRows([]);
    setSelectedCriticalRow(null);
  }, []);

  const closePlanDrawer = useCallback(() => {
    setPlanDrawerOpen(false);
    setSelectedPlanRow(null);
    setPlanCaseType('CORRECTION');
    setPlanning(false);
    setInspectorOptions([]);
    setInspectorOptionsLoading(false);
    setPlanningWeekStart(startOfWeekMonday(dayjs()));
    setPlanningContext(emptyPlanningContext);
    setPlanningContextLoading(false);
    setSelectedSlotKey(null);
    setSlotSelectionError(null);
    setContractFileList([]);
    planForm.resetFields();
  }, [planForm]);

  const closeComparisonDrawer = useCallback(() => {
    setComparisonDrawerOpen(false);
    setComparisonLoading(false);
    setComparison(null);
  }, []);

  useEffect(() => {
    if (!open) {
      closeCriticalDrawer();
      closePlanDrawer();
      closeComparisonDrawer();
    }
  }, [closeComparisonDrawer, closeCriticalDrawer, closePlanDrawer, open]);

  const handleListCriticalNonconformities = useCallback(async (row: ControlTaskReportApprovalRow) => {
    const taskId = Number(row?.taskId);
    const attributeId = Number(row?.attributeId);
    const orderNo = Number(row?.orderNo);
    if (
      proposalId == null
      || !row?.hasResponse
      || !Number.isFinite(taskId)
      || !Number.isFinite(attributeId)
      || !Number.isFinite(orderNo)
    ) {
      return;
    }

    const requestId = criticalRequestRef.current + 1;
    criticalRequestRef.current = requestId;
    setSelectedCriticalRow(row);
    setCriticalRows([]);
    setCriticalDrawerOpen(true);
    setCriticalLoading(true);

    try {
      const data = await listControlTaskCriticalNonconformities(proposalId, taskId, attributeId, orderNo);

      if (criticalRequestRef.current !== requestId) {
        return;
      }
      setCriticalRows((Array.isArray(data) ? data : [])
        .map((item: any, index: number) => ({
          questionId: String(item?.questionId ?? index),
          questionTitle: item?.questionTitle || '-',
          answerTitle: item?.answerTitle || '-',
          description: item?.description || '-',
          nodeTitle: item?.nodeTitle || '-',
          sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item.sortOrder) : index + 1,
          ...extractCriticalNonconformityImage(item?.sourceAnswerSnapshot),
          sourceAnswerSnapshot: item?.sourceAnswerSnapshot,
        }))
        .sort((left, right) => left.sortOrder - right.sortOrder));
    } catch (error) {
      if (criticalRequestRef.current !== requestId) {
        return;
      }
      console.error('Uygunsuzluklar yüklenemedi:', error);
      setCriticalRows([]);
      openNotification('error', 'Hata', 'Uygunsuzluklar yüklenemedi.');
    } finally {
      if (criticalRequestRef.current === requestId) {
        setCriticalLoading(false);
      }
    }
  }, [proposalId]);

  const loadPlanningContext = useCallback(async (row: ControlTaskReportApprovalRow, weekStartValue: dayjs.Dayjs) => {
    if (proposalId == null) {
      setInspectorOptions([]);
      setPlanningContext(emptyPlanningContext);
      return;
    }
    const attributeId = Number(row?.attributeId);
    if (!Number.isFinite(attributeId) || attributeId <= 0) {
      setInspectorOptions([]);
      setPlanningContext(emptyPlanningContext);
      return;
    }
    const requestId = planningContextRequestRef.current + 1;
    planningContextRequestRef.current = requestId;
    const startDate = weekStartValue.format('YYYY-MM-DD');
    const endDate = weekStartValue.add(6, 'day').format('YYYY-MM-DD');
    setInspectorOptionsLoading(true);
    setPlanningContextLoading(true);
    try {
      const context = await getPlanningInspectorContext(proposalId, startDate, endDate);
      if (planningContextRequestRef.current !== requestId) {
        return;
      }
      const normalizedContext = {
        inspectors: Array.isArray(context?.inspectors) ? context.inspectors : [],
        appointments: Array.isArray(context?.appointments) ? context.appointments : [],
        leaves: Array.isArray(context?.leaves) ? context.leaves : [],
        reservations: Array.isArray(context?.reservations) ? context.reservations : [],
      };
      setPlanningContext(normalizedContext);
      const options = (Array.isArray(context?.inspectors) ? context.inspectors : [])
        .filter((candidate) => candidateMatchesAttribute(candidate, attributeId))
        .map((candidate) => ({
          value: Number(candidate?.id),
          label: resolveInspectorOptionLabel(candidate),
        }))
        .filter((option) => Number.isFinite(option.value) && option.value > 0);
      const uniqueOptions = Array.from(
        new Map(options.map((option) => [option.value, option])).values(),
      ).sort((left, right) => textCollator.compare(left.label, right.label));
      setInspectorOptions(uniqueOptions);
    } catch (error) {
      if (planningContextRequestRef.current !== requestId) {
        return;
      }
      console.error('Planlama uygunluk bilgileri yüklenemedi:', error);
      setInspectorOptions([]);
      setPlanningContext(emptyPlanningContext);
      openNotification('error', 'Hata', 'Planlama uygunluk bilgileri yüklenemedi.');
    } finally {
      if (planningContextRequestRef.current === requestId) {
        setInspectorOptionsLoading(false);
        setPlanningContextLoading(false);
      }
    }
  }, [proposalId]);

  useEffect(() => {
    if (!planDrawerOpen || !selectedPlanRow) {
      return;
    }
    loadPlanningContext(selectedPlanRow, planningWeekStart);
  }, [loadPlanningContext, planDrawerOpen, planningWeekStart, selectedPlanRow]);

  useEffect(() => {
    if (!planDrawerOpen) {
      return;
    }
    let cancelled = false;
    setPublicHolidayLoading(true);
    listPublicHolidaysAll()
      .then((data) => {
        if (!cancelled) {
          setPublicHolidays(Array.isArray(data) ? data : []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Resmi tatil verileri yüklenemedi:', error);
          setPublicHolidays([]);
          openNotification('error', 'Hata', 'Resmi tatil verileri yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPublicHolidayLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [planDrawerOpen]);

  const openPlanDrawer = useCallback((row: ControlTaskReportApprovalRow, caseType: PlanCaseType = 'CORRECTION') => {
    const reportLabel = row?.reportNumber || row?.responseId || `${row?.taskId ?? ''}`;
    const followUpReplan = caseType === 'FOLLOW_UP' && Boolean(row?.followUpCaseId);
    setPlanCaseType(caseType);
    setSelectedPlanRow(row);
    setInspectorOptions([]);
    setPlanningContext(emptyPlanningContext);
    setPlanningWeekStart(startOfWeekMonday(dayjs()));
    setSelectedSlotKey(null);
    setSlotSelectionError(null);
    setContractFileList([]);
    planForm.setFieldsValue({
      assignedAccountId: undefined,
      plannedRange: undefined,
      note: caseType === 'FOLLOW_UP'
        ? `${followUpReplan ? 'Takip kontrolü tekrar planlama' : 'Takip kontrolü'} - ${reportLabel}`
        : `Uygunsuzluk düzeltme kontrolü - ${reportLabel}`,
      contractId: undefined,
      contractFile: undefined,
    });
    setPlanDrawerOpen(true);
  }, [planForm]);

  const submitCorrectionPlan = useCallback(async () => {
    if (!selectedPlanRow || proposalId == null) {
      return;
    }
    try {
      await planForm.validateFields();
      const values = planForm.getFieldsValue(true);
      const [plannedStart, plannedEnd] = values.plannedRange || [];
      if (!plannedStart || !plannedEnd) {
        setSlotSelectionError('Tarih ve saat seçiniz.');
        return;
      }
      setPlanning(true);
      let contractUrl: string | null = null;
      if (planCaseType === 'FOLLOW_UP') {
        const fileEntry = contractFileList[0];
        const fileObj = (fileEntry as any)?.originFileObj ?? fileEntry;
        if (!fileObj) {
          openNotification('warning', 'Uyarı', 'Sözleşme dosyası yükleyiniz.');
          setPlanning(false);
          return;
        }
        const uploaded = await DocumentService.postDocument(fileObj, 'appointment_contract');
        contractUrl = await extractUploadedDocumentUrl(uploaded);
        if (!contractUrl) {
          openNotification('error', 'Hata', 'Sözleşme yükleme başarısız.');
          setPlanning(false);
          return;
        }
      }
      await createControlTaskCorrection({
        proposalId,
        sourceResponseId: selectedPlanRow.responseId ?? null,
        sourceTaskId: selectedPlanRow.taskId ?? null,
        sourceAttributeId: selectedPlanRow.attributeId ?? null,
        sourceOrderNo: selectedPlanRow.orderNo ?? null,
        assignedAccountId: values.assignedAccountId ?? selectedPlanRow.accountId ?? null,
        caseType: planCaseType,
        plannedStartDate: plannedStart ? plannedStart.format('YYYY-MM-DDTHH:mm:ss') : null,
        plannedEndDate: plannedEnd ? plannedEnd.format('YYYY-MM-DDTHH:mm:ss') : null,
        contractId: planCaseType === 'FOLLOW_UP' ? values.contractId || null : null,
        contractUrl: planCaseType === 'FOLLOW_UP' ? contractUrl : null,
        note: values.note || null,
      });
      const refreshedRows = await listControlTaskReportApprovals(proposalId);
      updateRowsAndSummary(Array.isArray(refreshedRows) ? refreshedRows : []);
      const followUpPlanMessage = selectedPlanRow?.followUpCaseId
        ? 'Takip kontrolü tekrar planlandı.'
        : 'Takip kontrolü planlandı.';
      openNotification('success', 'Başarılı', planCaseType === 'FOLLOW_UP' ? followUpPlanMessage : 'Düzeltme planı oluşturuldu.');
      closePlanDrawer();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Plan oluşturulamadı:', error);
      openNotification('error', 'Hata', error?.message || 'Plan oluşturulamadı.');
    } finally {
      setPlanning(false);
    }
  }, [closePlanDrawer, contractFileList, planCaseType, planForm, proposalId, selectedPlanRow, updateRowsAndSummary]);

  const openComparisonDrawer = useCallback(async (row: ControlTaskReportApprovalRow) => {
    const caseId = Number(row?.correctionCaseId);
    if (!Number.isFinite(caseId) || caseId <= 0) {
      return;
    }
    setComparisonDrawerOpen(true);
    setComparisonLoading(true);
    setComparison(null);
    try {
      const data = await fetchControlTaskCorrectionComparison(caseId);
      setComparison(data);
    } catch (error) {
      console.error('Önce ve Sonra yüklenemedi:', error);
      openNotification('error', 'Hata', 'Önce ve Sonra bilgisi yüklenemedi.');
    } finally {
      setComparisonLoading(false);
    }
  }, []);

  const planningWeekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => planningWeekStart.add(index, 'day')),
    [planningWeekStart],
  );

  const blockingPublicHolidayDateSet = useMemo(
    () => buildBlockingPublicHolidayDateSet(publicHolidays),
    [publicHolidays],
  );

  const visiblePublicHolidayMap = useMemo(
    () => buildVisiblePublicHolidayMap(publicHolidays),
    [publicHolidays],
  );

  const selectedPlanningInspector = useMemo(() => {
    const accountId = Number(selectedPlanAccountId);
    if (!Number.isFinite(accountId) || accountId <= 0) {
      return null;
    }
    return (planningContext.inspectors || []).find((candidate) => {
      const candidateAccountId = Number(candidate?.account?.id ?? candidate?.id ?? candidate?.accountId);
      return Number.isFinite(candidateAccountId) && candidateAccountId === accountId;
    }) || null;
  }, [planningContext.inspectors, selectedPlanAccountId]);

  const getPlanSlotStatus = useCallback((slotStart: dayjs.Dayjs, slotEnd: dayjs.Dayjs): PlanSlotStatus => {
    const accountId = Number(selectedPlanAccountId);
    if (!Number.isFinite(accountId) || accountId <= 0) {
      return { disabled: true, reason: 'PKM seçiniz' };
    }

    const attributeId = Number(selectedPlanRow?.attributeId);
    if (
      selectedPlanningInspector &&
      Number.isFinite(attributeId) &&
      attributeId > 0 &&
      !candidateMatchesAttribute(selectedPlanningInspector, attributeId)
    ) {
      return { disabled: true, reason: 'Yetkisiz PKM' };
    }

    if (slotStart.isBefore(dayjs())) {
      return { disabled: true, reason: 'Geçmiş Saat' };
    }

    const dateKey = slotStart.format('YYYY-MM-DD');
    if (blockingPublicHolidayDateSet.has(dateKey)) {
      const holidayNames = (visiblePublicHolidayMap.get(dateKey) || [])
        .filter((holiday) => holiday?.halfDay !== true)
        .map((holiday) => holiday?.name?.toString().trim())
        .filter(Boolean) as string[];
      return {
        disabled: true,
        reason: 'Resmi Tatil',
        details: holidayNames.length ? holidayNames : ['Resmi Tatil'],
      };
    }

    const overlappingLeave = (planningContext.leaves || []).find((leave) => {
      if (resolveEntryAccountId(leave) !== accountId) {
        return false;
      }
      const leaveStart = dayjs(leave?.startDate);
      const leaveEnd = leave?.endDate ? dayjs(leave.endDate) : leaveStart;
      const interval = buildBusyInterval(leaveStart, leaveEnd, { allDay: true });
      return interval ? isTimeRangeOverlapping(slotStart, slotEnd, interval.start, interval.end) : false;
    });
    if (overlappingLeave) {
      return {
        disabled: true,
        reason: 'İzin',
        details: [resolveLeaveLabel(overlappingLeave)],
      };
    }

    const overlappingReservation = (planningContext.reservations || []).find((reservation) => {
      if (resolveEntryAccountId(reservation) !== accountId) {
        return false;
      }
      const reservationStartRaw =
        reservation?.reservationDate ??
        reservation?.date ??
        reservation?.startDate ??
        reservation?.reservationStartDate;
      const reservationStart = dayjs(reservationStartRaw);
      const reservationEndRaw = reservation?.endDate ?? reservation?.reservationEndDate;
      const reservationEnd = reservationEndRaw ? dayjs(reservationEndRaw) : reservationStart;
      const interval = buildBusyInterval(reservationStart, reservationEnd, { allDay: true });
      return interval ? isTimeRangeOverlapping(slotStart, slotEnd, interval.start, interval.end) : false;
    });
    if (overlappingReservation) {
      const note = normalizeText(overlappingReservation?.note);
      return {
        disabled: true,
        reason: 'Rezervasyon',
        details: note ? [note] : ['Rezervasyon'],
      };
    }

    const overlappingAppointment = (planningContext.appointments || []).find((appointment) => {
      if (isDeletedOrCanceledAppointment(appointment) || resolveEntryAccountId(appointment) !== accountId) {
        return false;
      }
      const appointmentStart = dayjs(appointment?.startDate ?? appointment?.planningAt ?? appointment?.start_date);
      const appointmentEndRaw = appointment?.endDate ?? appointment?.planningEndDate ?? appointment?.end_date;
      const appointmentEnd = appointmentEndRaw ? dayjs(appointmentEndRaw) : appointmentStart;
      const interval = buildBusyInterval(appointmentStart, appointmentEnd);
      return interval ? isTimeRangeOverlapping(slotStart, slotEnd, interval.start, interval.end) : false;
    });
    if (overlappingAppointment) {
      const start = dayjs(overlappingAppointment?.startDate ?? overlappingAppointment?.planningAt ?? overlappingAppointment?.start_date);
      const endRaw = overlappingAppointment?.endDate ?? overlappingAppointment?.planningEndDate ?? overlappingAppointment?.end_date;
      const end = endRaw ? dayjs(endRaw) : null;
      return {
        disabled: true,
        reason: 'Görev',
        details: [
          resolveAppointmentLabel(overlappingAppointment),
          start.isValid() ? `${start.format('HH:mm')} - ${end?.isValid() ? end.format('HH:mm') : '--:--'}` : '',
        ].filter(Boolean),
      };
    }

    return { disabled: false };
  }, [
    blockingPublicHolidayDateSet,
    planningContext.appointments,
    planningContext.leaves,
    planningContext.reservations,
    selectedPlanAccountId,
    selectedPlanningInspector,
    selectedPlanRow,
    visiblePublicHolidayMap,
  ]);

  const clearSelectedPlanSlot = useCallback(() => {
    setSelectedSlotKey(null);
    setSlotSelectionError(null);
    planForm.setFieldValue('plannedRange', undefined);
  }, [planForm]);

  const movePlanningWeek = useCallback((deltaWeek: number) => {
    clearSelectedPlanSlot();
    setPlanningWeekStart((current) => current.add(deltaWeek * 7, 'day'));
  }, [clearSelectedPlanSlot]);

  const selectPlanSlot = useCallback((slotStart: dayjs.Dayjs, slotEnd: dayjs.Dayjs) => {
    const status = getPlanSlotStatus(slotStart, slotEnd);
    if (status.disabled) {
      return;
    }
    const slotKey = `${slotStart.format('YYYY-MM-DDTHH:mm')}-${slotEnd.format('YYYY-MM-DDTHH:mm')}`;
    setSelectedSlotKey(slotKey);
    setSlotSelectionError(null);
    planForm.setFieldValue('plannedRange', [slotStart, slotEnd]);
  }, [getPlanSlotStatus, planForm]);

  const planGridLoading = planningContextLoading || publicHolidayLoading;

  const columns = useMemo(() => [
    {
      title: 'PKM Uzmanı',
      dataIndex: 'inspectorName',
      key: 'inspectorName',
      render: (value: string | null | undefined) => <Text>{value || '-'}</Text>,
    },
    {
      title: 'Parametre',
      dataIndex: 'attributeName',
      key: 'attributeName',
      render: (value: string | null | undefined, row: ControlTaskReportApprovalRow) => (
        <Space direction="vertical" size={2}>
          <Text>{value || '-'}</Text>
          {row?.responseKind === 'CORRECTION' ? (
            row?.followUpCaseId ? <Tag color="geekblue">TAKİP KONTROLÜ</Tag> : <Tag color="purple">DÜZELTME KONTROLÜ</Tag>
          ) : null}
          {row?.sourceResponseId ? <Text type="secondary">Önceki Rapor: {row.sourceResponseId}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Kontrol Sırası',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 90,
      render: (value: number | null | undefined) => <Text>{value ?? '-'}</Text>,
    },
    {
      title: 'Kontrol',
      key: 'controlCompleted',
      width: 110,
      render: (_: any, row: ControlTaskReportApprovalRow) => (
        row?.controlCompleted ? <Tag color="blue">Tamamlandı</Tag> : <Tag>Bekliyor</Tag>
      ),
    },
    {
      title: 'Rapor Onayı',
      key: 'reportApproved',
      width: 120,
      render: (_: any, row: ControlTaskReportApprovalRow) => (
        <Space direction="vertical" size={2}>
          {row?.reportApproved ? <Tag color="green">Onaylı</Tag> : <Tag color="orange">Bekliyor</Tag>}
          {row?.reportNumber ? <Text type="secondary">{row.reportNumber}</Text> : null}
          {row?.correctionStatus ? renderCorrectionStatusTag(row.correctionStatus) : null}
          {row?.followUpStatus ? <Tag color="geekblue">Takip: {correctionStatusLabelMap[row.followUpStatus] || row.followUpStatus}</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 430,
      render: (_: any, row: ControlTaskReportApprovalRow) => {
        const rowKey = getRowKey(row);
        const canAct = Boolean(row?.hasResponse)
          && row?.controlCompleted === true
          && row?.taskId != null
          && row?.attributeId != null
          && row?.orderNo != null;
        return (
          <Space wrap>
            {row?.reportApproved ? (
              <Popconfirm
                title="Rapor onayını geri almak istediğinize emin misiniz?"
                okText="Evet"
                cancelText="Hayır"
                onConfirm={() => handleRevoke(row)}
              >
                <Button
                  size="small"
                  danger
                  loading={actionKey === rowKey}
                  disabled={!canAct}
                >
                  Onayı Geri Al
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Raporu onaylamak istediğinize emin misiniz?"
                okText="Evet"
                cancelText="Hayır"
                onConfirm={() => handleApprove(row)}
              >
                <Button
                  size="small"
                  type="primary"
                  loading={actionKey === rowKey}
                  disabled={!canAct}
                >
                  Onayla
                </Button>
              </Popconfirm>
            )}
            <Button
              size="small"
              onClick={() => handleDownload(row)}
              loading={downloadKey === rowKey}
              disabled={!canAct}
            >
              Raporu İndir
            </Button>
            {row?.controlCompleted && row?.hasCriticalNonconformity ? (
              <Button
                size="small"
                onClick={() => handleListCriticalNonconformities(row)}
                disabled={!canAct}
              >
                Uygunsuzlukları Listele
              </Button>
            ) : null}
            {/* {row?.hasCriticalNonconformity && !row?.hasOpenCorrection ? (
              <Button
                size="small"
                onClick={() => openPlanDrawer(row, 'CORRECTION')}
                disabled={!canAct}
              >
                Düzeltme Planla
              </Button>
            ) : null}
            {row?.responseKind !== 'CORRECTION' && row?.controlCompleted && !row?.hasOpenFollowUp && !isOpenPlanStatus(row?.followUpStatus) ? (
              <Button
                size="small"
                onClick={() => openPlanDrawer(row, 'FOLLOW_UP')}
                disabled={!canAct}
              >
                {row?.followUpCaseId ? 'Takip Kontrolünü Tekrar Planla' : 'Takip Kontrolü Planla'}
              </Button>
            ) : null} */}
            {/* {row?.correctionCaseId ? (
              <Button
                size="small"
                onClick={() => openComparisonDrawer(row)}
                disabled={!canAct}
              >
                Önce ve Sonra
              </Button>
            ) : null} */}
          </Space>
        );
      },
    },
  ], [actionKey, downloadKey, handleApprove, handleDownload, handleListCriticalNonconformities, handleRevoke, openComparisonDrawer, openPlanDrawer]);

  const criticalColumns = useMemo(() => [
        {
      title: 'Bölüm',
      dataIndex: 'nodeTitle',
      key: 'nodeTitle',
      width: 180,
      render: (value: string) => <Text>{value || '-'}</Text>,
    },
    {
      title: 'Soru - Uygunsuzluk Nedeni',
      dataIndex: 'questionTitle',
      key: 'questionTitle',
      render: (_: string, row: CriticalNonconformityRow) => (
        <Space direction="vertical" size={2}>
          <Text>{`${row.sortOrder}. ${row.questionTitle || '-'}`}</Text>
          <Text type="secondary">{row.description || '-'}</Text>
          <Tag color="red">{row.answerTitle || '-'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Fotoğraf',
      key: 'photo',
      width: 110,
      render: (_: any, row: CriticalNonconformityRow) => <CriticalNonconformityImagePreview row={row} />,
    },
    {
      title: 'Düzeltme Durumu',
      key: 'correctionStatus',
      width: 150,
      render: () => (
        renderCorrectionStatusTag(selectedCriticalRow?.correctionStatus)
      ),
    }
  ], [selectedCriticalRow]);

  return (
    <>
      <Drawer
        title={proposalNumber ? `Rapor Onayları - ${proposalNumber}` : 'Rapor Onayları'}
        open={open}
        onClose={() => {
          closeCriticalDrawer();
          onClose();
        }}
        width={1100}
        destroyOnClose
      >
        <Table
          rowKey={(row) => getRowKey(row)}
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          locale={{ emptyText: <Empty description="Görev bulunamadı." /> }}
          scroll={{ x: 1050 }}
        />
      </Drawer>
      <Drawer
        title={
          selectedCriticalRow?.attributeName
            ? `Uygunsuzluklar - ${selectedCriticalRow.attributeName}`
            : 'Uygunsuzluklar'
        }
        open={criticalDrawerOpen}
        onClose={closeCriticalDrawer}
        width={1000}
        destroyOnClose
      >
        <Space style={{ marginBottom: 12 }} wrap>
          {selectedCriticalRow?.hasCriticalNonconformity && !selectedCriticalRow?.hasOpenCorrection ? (
            <Button type="primary" onClick={() => openPlanDrawer(selectedCriticalRow, 'CORRECTION')}>
              Düzeltme Planla
            </Button>
          ) : null}
          {selectedCriticalRow?.correctionCaseId ? (
            <Button onClick={() => openComparisonDrawer(selectedCriticalRow)}>
              Önce ve Sonra
            </Button>
          ) : null}
        </Space>
        <Table
          rowKey={(row) => `${row.questionId}-${row.answerTitle}`}
          loading={criticalLoading}
          dataSource={criticalRows}
          columns={criticalColumns}
          pagination={false}
          locale={{ emptyText: <Empty description="Uygunsuzluk bulunamadı." /> }}
          scroll={{ x: 940 }}
        />
      </Drawer>
      <Drawer
        title={planCaseType === 'FOLLOW_UP'
          ? (selectedPlanRow?.followUpCaseId ? 'Takip Kontrolünü Tekrar Planla' : 'Takip Kontrolü Planı')
          : 'Düzeltme Planı'}
        open={planDrawerOpen}
        onClose={closePlanDrawer}
        width={1040}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closePlanDrawer}>İptal</Button>
            <Button type="primary" loading={planning} onClick={submitCorrectionPlan}>Kaydet</Button>
          </Space>
        }
      >
        <Form form={planForm} layout="vertical">
          <Form.Item name="assignedAccountId" label="Uzman" rules={[{ required: true, message: 'Uzman seçiniz.' }]}>
            <Select
              showSearch
              placeholder="Uzman seçiniz"
              options={inspectorOptions}
              loading={inspectorOptionsLoading}
              notFoundContent={inspectorOptionsLoading ? 'Yükleniyor...' : 'Uygun PKM bulunamadı'}
              onChange={() => {
                clearSelectedPlanSlot();
              }}
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
              }
            />
          </Form.Item>
          <Form.Item
            label="Müsait Gün / Saat"
            validateStatus={slotSelectionError ? 'error' : undefined}
            help={slotSelectionError || undefined}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Button onClick={() => movePlanningWeek(-1)}>Önceki Hafta</Button>
                <Text strong>{formatWeekRange(planningWeekStart)}</Text>
                <Button onClick={() => movePlanningWeek(1)}>Sonraki Hafta</Button>
              </div>
              {planGridLoading ? (
                <Text type="secondary">Uygunluk bilgileri yükleniyor...</Text>
              ) : null}
              <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 }}>
                <table
                  data-testid="weekly-plan-grid"
                  style={{ width: '100%', minWidth: 860, borderCollapse: 'separate', borderSpacing: 0 }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 112, padding: '10px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'left', background: '#fafafa' }}>
                        Saat
                      </th>
                      {planningWeekDays.map((day, index) => {
                        const dateKey = day.format('YYYY-MM-DD');
                        const isHoliday = blockingPublicHolidayDateSet.has(dateKey);
                        return (
                          <th
                            key={dateKey}
                            style={{
                              minWidth: 104,
                              padding: '10px 8px',
                              borderBottom: '1px solid #f0f0f0',
                              textAlign: 'center',
                              background: isHoliday ? '#fff1f0' : '#fafafa',
                              color: isHoliday ? '#cf1322' : undefined,
                            }}
                          >
                            <div>{planDayNames[index]}</div>
                            <div style={{ fontSize: 12, fontWeight: 400 }}>{day.format('DD.MM')}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {planHourSlots.map((hourSlot) => (
                      <tr key={hourSlot.hour}>
                        <td style={{ padding: 8, borderBottom: '1px solid #f5f5f5', background: '#fff', fontWeight: 600 }}>
                          {hourSlot.label}
                        </td>
                        {planningWeekDays.map((day) => {
                          const slotStart = day.hour(hourSlot.hour).minute(0).second(0).millisecond(0);
                          const slotEnd = slotStart.add(1, 'hour');
                          const dateKey = day.format('YYYY-MM-DD');
                          const slotKey = `${slotStart.format('YYYY-MM-DDTHH:mm')}-${slotEnd.format('YYYY-MM-DDTHH:mm')}`;
                          const status = planGridLoading
                            ? { disabled: true, reason: 'Yükleniyor' }
                            : getPlanSlotStatus(slotStart, slotEnd);
                          const isSelected = selectedSlotKey === slotKey;
                          const details = status.details?.filter(Boolean).join(' / ');
                          const tooltipTitle = status.reason
                            ? [status.reason, details].filter(Boolean).join(' - ')
                            : `${slotStart.format('DD.MM.YYYY HH:mm')} - ${slotEnd.format('HH:mm')}`;
                          return (
                            <td key={`${dateKey}-${hourSlot.hour}`} style={{ padding: 6, borderBottom: '1px solid #f5f5f5' }}>
                              <Tooltip title={tooltipTitle}>
                                <span style={{ display: 'block' }}>
                                  <Button
                                    block
                                    size="small"
                                    type={isSelected ? 'primary' : 'default'}
                                    disabled={status.disabled}
                                    data-testid={`plan-slot-${dateKey}-${hourSlot.hour}`}
                                    onClick={() => selectPlanSlot(slotStart, slotEnd)}
                                    style={{
                                      minHeight: 42,
                                      height: 'auto',
                                      whiteSpace: 'normal',
                                      padding: '5px 6px',
                                      opacity: status.disabled ? 0.72 : 1,
                                    }}
                                  >
                                    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                      <span>{slotStart.format('HH:mm')} - {slotEnd.format('HH:mm')}</span>
                                      {status.reason ? (
                                        <span style={{ fontSize: 11 }}>{status.reason}</span>
                                      ) : null}
                                    </span>
                                  </Button>
                                </span>
                              </Tooltip>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!selectedPlanAccountId ? (
                <Text type="secondary">PKM seçildikten sonra uygun saat seçilebilir.</Text>
              ) : null}
            </Space>
          </Form.Item>
          {planCaseType === 'FOLLOW_UP' ? (
            <>
              <Form.Item
                name="contractId"
                label="Sözleşme No"
                rules={[{ required: true, message: 'Sözleşme numarası zorunludur.' }]}
              >
                <Input placeholder="Sözleşme numarası giriniz" />
              </Form.Item>
              <Form.Item
                name="contractFile"
                label="Sözleşme Dosyası"
                rules={[{ required: true, message: 'Sözleşme dosyası yükleyiniz.' }]}
              >
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  fileList={contractFileList}
                  onChange={({ fileList }) => {
                    setContractFileList(fileList);
                    planForm.setFieldValue('contractFile', fileList.length ? fileList : undefined);
                  }}
                >
                  <Button>Sözleşme Yükle</Button>
                </Upload>
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="note" label="Not">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
      <Drawer
        title="Önce ve Sonra"
        open={comparisonDrawerOpen}
        onClose={closeComparisonDrawer}
        width={950}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap>
            <Tag>Önceki Rapor: {comparison?.sourceReportNumber || comparison?.sourceResponseId || '-'}</Tag>
            <Tag color="purple">Düzeltme Raporu: {comparison?.correctionReportNumber || comparison?.correctionResponseId || '-'}</Tag>
          </Space>
          <Table
            rowKey={(row) => String(row?.questionId)}
            loading={comparisonLoading}
            dataSource={Array.isArray(comparison?.rows) ? comparison.rows : []}
            pagination={false}
            locale={{ emptyText: <Empty description="Karşılaştırma bulunamadı." /> }}
            columns={[
              {
                title: 'Bölüm',
                dataIndex: 'nodeTitle',
                key: 'nodeTitle',
                width: 170,
                render: (value: string) => <Text>{value || '-'}</Text>,
              },
              {
                title: 'Soru',
                dataIndex: 'questionTitle',
                key: 'questionTitle',
                render: (value: string) => <Text>{value || '-'}</Text>,
              },
              {
                title: 'Buydu',
                dataIndex: 'beforeAnswer',
                key: 'beforeAnswer',
                width: 190,
                render: (value: string, row: any) => (
                  <Space direction="vertical" size={2}>
                    <Tag color="red">{value || '-'}</Tag>
                    {row?.beforeDescription ? <Text type="secondary">{row.beforeDescription}</Text> : null}
                  </Space>
                ),
              },
              {
                title: 'Bu Oldu',
                dataIndex: 'afterAnswer',
                key: 'afterAnswer',
                width: 190,
                render: (value: string, row: any) => (
                  <Space direction="vertical" size={2}>
                    <Tag color="green">{value || '-'}</Tag>
                    {row?.afterDescription ? <Text type="secondary">{row.afterDescription}</Text> : null}
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      </Drawer>
    </>
  );
};

export default ReportApprovalDrawer;
